import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import { TableLoader } from "../../components/TableLoader";
import { TableNodata } from "../../components/TableNodata";
import { Cancel, Close } from "@mui/icons-material";
import CategoryIcon from "@mui/icons-material/Category";
import { useSnackbar } from "../../providers/SnackbarProvider";
import BookmarkRemoveIcon from "@mui/icons-material/BookmarkRemove";
import { OverAllLoader } from "../../components/OverAllLoader";
import EditIcon from "@mui/icons-material/Edit";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const ViewInstitute = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const tableHeadings = [
    { label: "S.No", align: "center" },
    { label: `${getLabel("SUBJECT")} Name`, align: "left" },
    { label: "Assigned By", align: "left" },
    { label: "Assigned At", align: "left" },
    { label: "Action", align: "center" },
  ];

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [assignedCategories, setAssignedCategories] = useState([]);
  const [count, setCount] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const location = useLocation();
  const navigate = useNavigate();
  const [overAllLoading, setOverAllLoading] = useState(false);
  const [instituteData, setInstituteData] = useState({}); // ✅ Corrected
  const { showSnackbar } = useSnackbar();
  const { institute_id } = location?.state || {};

  const fetchById = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("ViewInstituteByID", {
        institute_id: institute_id,
      });
      if (res?.data?.status === "success") {
        setInstituteData(res?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching institute by ID:", error);
    } finally {
      setOverAllLoading(false);
    }
  };
  const listCategory = async () => {
    try {
      const res = await api.post("CategoryListForInstituteAssignment", {
        institute_id: institute_id,
      });
      if (res.data.status === "success") {
        setCategories(res.data.data); // store categories array
      }
    } catch (error) {
      console.log("Error fetching categories:", error);
    }
  };

  const handleAssign = async () => {
    setOverAllLoading(true);

    try {
      const res = await api.post("AssignInstituteToCategory", {
        institute_ids: [institute_id],
        category_ids: selectedCategories,
      });
      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message);
        setOpenDialog(false);
        assignedList();
      } else {
        showSnackbar("error", res?.data?.message);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to assign categories. Please try again!");
    } finally {
      setOverAllLoading(false);
    }
  };

  const assignedList = async () => {
    try {
      const payload = {
        page: page || 0,
        size: rowsPerPage || 5,
        search_key: searchKey || "",
        institute_id: institute_id,
      };
      const res = await api.post("ListAssignedCategories", payload);
      if (res?.data?.status === "success") {
        setAssignedCategories(res.data.data || []);
        setCount(res?.data?.count);
      }
    } catch (error) {
      console.error("Error fetching assigned categories:", error);
    }
  };

  const handleUnassign = async (id) => {
    setOverAllLoading(true);
    try {
      const res = await api.post("UnassignInstituteFromCategory", {
        institute_id: institute_id,
        category_id: id,
      });

      if (res?.data?.status === "success") {
        showSnackbar("success", res?.data?.message);
        assignedList();
      } else if (res?.data?.status === "error") {
        showSnackbar("error", res?.data?.message);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to unassign category. Please try again!");
    } finally {
      setOverAllLoading(false);
    }
  };

  useEffect(() => {
    fetchById();
  }, []);

  useEffect(() => {
    assignedList();
  }, [page, rowsPerPage, searchKey]);

  // Pagination Handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message={`Fetching ${getLabel("INSTITUTE")} details...`} />
      ) : (
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "fit-content",
              mb: 2,
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          {/* View Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  mb: 2,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Grid
                  sx={{
                    backgroundColor: "#1c97e3",
                    p: 0.5,
                    borderRadius: "10px",
                  }}
                >
                  <GroupIcon sx={{ color: "white", fontSize: "35px" }} />
                </Grid>
                <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                  {getLabel("INSTITUTE")} Details
                </Typography>
              </Grid>
              <Grid>
                <Button
                  variant="outlined"
                  sx={{ whiteSpace: "nowrap", mr: 4 }}
                  onClick={() => (
                    setSelectedCategories([]),
                    setOpenDialog(true),
                    listCategory()
                  )}
                >
                  Assign {getLabel("SUBJECT")}
                </Button>
                <Button
                  variant="contained"
                  className="gradient-button"
                  startIcon={<EditIcon />}
                  onClick={() =>
                    navigate("/addInstitute", {
                      state: {
                        institute_id: institute_id,
                      },
                    })
                  }
                >
                  Edit
                </Button>
              </Grid>
            </Grid>

            {/* Details */}
            <Box
              sx={{ p: 3, display: "flex", flexDirection: "column", rowGap: 3 }}
            >
              {/* Basic Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GroupIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid
                sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}
              >
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">{getLabel("INSTITUTE")} Name</Typography>
                    <Typography>{instituteData.name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Display Name</Typography>
                    <Typography>{instituteData.short_name || "-"}</Typography>
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Created By</Typography>
                    <Typography>{instituteData.created_by || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Created Date</Typography>
                    <Typography>
                      {instituteData.created_at
                        ? dayjs(instituteData.created_at).format("DD-MM-YYYY")
                        : "-"}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Updated By</Typography>
                    <Typography>{instituteData.updated_by || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Updated Date</Typography>
                    <Typography>
                      {instituteData.updated_at
                        ? dayjs(instituteData.updated_at).format("DD-MM-YYYY")
                        : "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Status</Typography>
                    <Typography>{instituteData.status || "-"}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Contact Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MailOutlineIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Contact Information
                </Typography>
              </Grid>

              <Grid sx={{ display: "flex" }}>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">Contact Number</Typography>
                  <Typography>{instituteData.contact_phone || "-"}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">Email</Typography>
                  <Typography>{instituteData.contact_email || "-"}</Typography>
                </Grid>
              </Grid>

              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MailOutlineIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Address Information
                </Typography>
              </Grid>

              <Grid sx={{ display: "flex" }}>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">Address</Typography>
                  <Typography>{instituteData.address || "-"}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">Pincode</Typography>
                  <Typography>{instituteData.pincode || "-"}</Typography>
                </Grid>
              </Grid>

              <Grid sx={{ display: "flex" }}>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">City</Typography>
                  <Typography>{instituteData.city || "-"}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ width: "50%" }}>
                  <Typography fontWeight="600">state</Typography>
                  <Typography>{instituteData.state || "-"}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box mt={3}>
            {/* Header Row */}
            <Grid
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="h6" fontWeight={600}>
                Assigned {getLabel("SUBJECT")}
              </Typography>
              <TextField
                value={searchKey}
                onChange={(e) => (setPage(0), setSearchKey(e.target.value))}
                size="small"
                variant="outlined"
                placeholder={`Search ${getLabel("SUBJECT")}`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Table Section */}
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                overflow: "hidden",
              }}
            >
              <TableContainer
                sx={{
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      {tableHeadings.map((heading, index) => (
                        <TableCell
                          key={index}
                          align={heading.align}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            color: "text.secondary",
                            borderBottom: "2px solid",
                            borderColor: "divider",
                            backgroundColor: "#e8f0fe",
                          }}
                        >
                          {heading.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  {loading ? (
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={tableHeadings.length}
                          align="center"
                        >
                          <TableLoader message="Fetching data..." />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {assignedCategories.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={tableHeadings.length}
                            align="center"
                            sx={{ py: 3 }}
                          >
                            <TableNodata />
                          </TableCell>
                        </TableRow>
                      ) : (
                        assignedCategories.map((item, index) => (
                          <TableRow key={item.id} hover>
                            <TableCell align="center">
                              {page * rowsPerPage + index + 1}
                            </TableCell>
                            <TableCell>{item.category_name}</TableCell>
                            <TableCell>{item.created_by}</TableCell>
                            <TableCell>
                              {item.created_at
                                ? dayjs(item.created_at).format("DD-MM-YYYY")
                                : "-"}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip
                                title={"Unassign " + getLabel("SUBJECT")}
                                arrow
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "primary.main",
                                      color: "white",
                                      fontSize: 13,
                                      fontWeight: 500,
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: "primary.main",
                                    },
                                  },
                                }}
                              >
                                <IconButton
                                  color="error"
                                  size="small"
                                  disabled={overAllLoading}
                                  onClick={() =>
                                    handleUnassign(item.category_id)
                                  }
                                >
                                  <BookmarkRemoveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>

              {((page === 0 && assignedCategories?.length > rowsPerPage - 1) ||
                page !== 0) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 2,
                    }}
                  >
                    {page !== 0 ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={
                            <ArrowBackIcon
                              sx={{
                                color: page === 0 ? "#a2a2a2" : "#414651",
                              }}
                            />
                          }
                          disabled={page === 0}
                          onClick={() => setPage(page - 1)}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            color: page === 0 ? "#a2a2a2" : "#414651",
                            borderColor: "#d0d0d0",
                          }}
                        >
                          Previous
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ width: "110px" }} />
                    )}

                    <Pagination
                      page={page + 1}
                      onChange={(e, value) => setPage(value - 1)}
                      count={Math.ceil(count / rowsPerPage)}
                      siblingCount={3}
                      boundaryCount={3}
                      shape="rounded"
                      hidePrevButton
                      hideNextButton
                    />
                    {page + 1 !== Math.ceil(count / rowsPerPage) ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                          variant="outlined"
                          endIcon={
                            <ArrowForwardIcon
                              sx={{
                                color: page + 1 === count ? "#a2a2a2" : "#414651",
                              }}
                            />
                          }
                          disabled={page + 1 === count}
                          onClick={() => setPage(page + 1)}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            color: page + 1 === count ? "#a2a2a2" : "#414651",
                            borderColor: "#d0d0d0",
                          }}
                        >
                          Next
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ width: "110px" }} />
                    )}
                  </Box>
                )}
            </Paper>
          </Box>

          <Dialog
            open={openDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
              },
            }}
          >
            {/* Header */}
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "linear-gradient(90deg, #f5f9ff, #eef4fb)",
                borderBottom: "1px solid",
                borderColor: "divider",
                py: 2,
                px: 3,
                mb: 2,
              }}
            >
              <Box display="flex" alignItems="center">
                <CategoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Assign {getLabel("SUBJECT")}
                </Typography>
              </Box>
              <IconButton onClick={() => setOpenDialog(false)} size="small">
                <Close />
              </IconButton>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ px: 3, py: 3 }}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={categories}
                getOptionLabel={(option) => option.category_name || ""}
                isOptionEqualToValue={(option, value) =>
                  option.category_id === value.category_id
                }
                value={
                  selectedCategories
                    .map((id) => categories.find((c) => c.category_id === id))
                    .filter(Boolean) // ✅ remove undefined
                }
                onChange={(event, newValue) =>
                  setSelectedCategories(
                    newValue ? newValue.map((item) => item.category_id) : []
                  )
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props} style={{ padding: "8px 14px" }}>
                    <Checkbox
                      checked={selectedCategories.includes(option.category_id)}
                    />
                    <Typography variant="body1" fontWeight={500}>
                      {option.category_name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={`Select ${getLabel("SUBJECT")}`}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <CategoryIcon
                            sx={{ color: "action.active", mr: 1, ml: 0.5 }}
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </DialogContent>

            {/* Actions */}
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                backgroundColor: "grey.50",
              }}
            >
              <Button
                onClick={() => setOpenDialog(false)}
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAssign}
                startIcon={<BusinessIcon />}
                disabled={selectedCategories.length === 0 || overAllLoading}
              >
                Assign{" "}
                {selectedStudents.length > 0
                  ? `(${selectedStudents.length})`
                  : ""}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default ViewInstitute;
